import React, { useEffect, useCallback, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import InputField from 'components/shared/input-field';
import PhoneNumberInput from 'components/shared/input-field/phoneInput';
import Button from 'components/shared/button';
import { Loader } from 'lucide-react';
import CountrySelect from 'components/shared/select-input/countryInput';
import SelectInput from 'components/shared/select-input';
import TermsModal from 'components/shared/modal/TermsModal';

import TermsContent from 'components/shared/TermsContent';
import useFetchStates from 'hooks/useFetchStates';
import PaySlipFileUpload from 'components/shared/input-field/PaySlipField';
import CustomFileUpload from 'components/shared/input-field/FileUpload';
import { OptionType } from 'types';
import Checkbox from 'components/shared/checkbox';

interface EmploymentInfo {
  Occupation: string;
  EmployersName: string;
  MonthsOfWorkinWithThem: string;
  YearsOfWorkinWithThem: string;
  OfficialEmail: string;
  HrContactPhoneNumber: string;
  EmployerContactAddress1: string;
  EmployerAddress1: string;
  EmployerAddress2: string;
  City2: string;
  State2: string;
  PostalCode2: string;
  Country2: string;
  PaySlip: File | null;
  BankStatement: File | null;
  TermsCondition: boolean;
  // LoanAgreement: boolean;
}

interface Step3Props {
  inputData: EmploymentInfo;
  setInputData: (data: EmploymentInfo) => void;
  errors: { [key: string]: string };
  clearErrors: (field: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  loading: boolean;
}

const validationSchema = yup.object({
  Occupation: yup.string().required('Occupation is required'),
  EmployersName: yup.string().required("Employer's name is required"),
  MonthsOfWorkinWithThem: yup
    .string()
    .required('Months of working with employer is required'),
  YearsOfWorkinWithThem: yup
    .string()
    .required('Years of working with employer is required'),
  OfficialEmail: yup
    .string()
    .email('Invalid email format')
    .required('Official email is required'),
  HrContactPhoneNumber: yup
    .string()
    .required('HR contact phone number is required')
    .matches(/^[0-9]{13}$/, 'Phone number must be 13 digits including 234'),
  EmployerContactAddress1: yup
    .string()
    .required("Employer's contact address is required"),
  City2: yup.string().required('City is required'),
  // State2: yup.string().required('State is required'),
  // PostalCode2: yup.string().required('Postal code is required'),
  Country2: yup.string().required('Country is required'),
  PaySlip: yup
    .mixed()
    .nullable()
    .notRequired()
    .test(
      'fileSize',
      'File size is too large. Maximum size is 5MB.',
      (file: any) => {
        return file ? file.size <= 5 * 1024 * 1024 : true;
      }
    ),

  BankStatement: yup
    .mixed()
    .required('Bank statement is required')
    .test(
      'fileSize',
      'File size is too large. Maximum size is 5MB.',
      (file: any) => {
        return file ? file.size <= 5 * 1024 * 1024 : true;
      }
    ),
});

const MySelfStep2: React.FC<Step3Props> = ({
  inputData,
  setInputData,
  errors,
  clearErrors,
  nextStep,
  prevStep,
  loading,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoanModalOpen, setIsLoanModalOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isLoanChecked, setIsLoanChecked] = useState(false);

  const formik = useFormik({
    initialValues: inputData,
    validationSchema,
    onSubmit: (values) => {
      setInputData(values);
      clearErrors('');
    },
  });

  useEffect(() => {
    setInputData(formik.values);
  }, [formik.values]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === 'MonthsOfWorkinWithThem') {
      const numericValue = Math.max(1, Math.min(12, Number(value)));
      formik.setFieldValue(name, numericValue);
    } else if (name === 'State2') {
      const selectedOption = stateOptions.find(
        (option) => option.value === value
      );
      if (selectedOption) {
        formik.setFieldValue(name, selectedOption.label); // Set the label (name) as the value
      }
    } else {
      formik.handleChange(e);
    }
    clearErrors(name);
  };

  const handleModalConfirm = (isChecked: boolean) => {
    setIsModalOpen(false);
    // Set the checkbox to true when modal is confirmed
    setIsChecked(isChecked);
    formik.setFieldValue('TermsCondition', isChecked, true);
    // inputData.TermsCondition = isChecked;
  };

  const handleLoanModalConfirm = (isLoanChecked: boolean) => {
    setIsLoanModalOpen(!isLoanChecked);
    setIsLoanChecked(isLoanChecked);
    formik.setFieldValue('LoanAgreement', isLoanChecked, true);
  };

  const handleCheckboxChange = () => {
    if (isChecked) {
      setIsChecked(false);
      formik.setFieldValue('TermsCondition', false);
    } else {
      setIsModalOpen(true);
      formik.setFieldValue('TermsCondition', true);
    }
  };

  const handleLoanCheckboxChange = () => {
    if (isLoanChecked) {
      setIsLoanChecked(false);
      formik.setFieldValue('LoanAgreement', false);
    } else {
      setIsLoanModalOpen(true);
      formik.setFieldValue('LoanAgreement', true);
    }
  };

  const onDropBankStatements = useCallback(
    (acceptedFiles: File[]) => {
      const currentFile = formik.values?.BankStatement || null;

      // Process the new file
      const filteredFile = acceptedFiles.find(
        (file) => file.size <= 5 * 1024 * 1024
      );
      if (!filteredFile) {
        formik.setFieldError(
          'ParentRecords_BankStatement',
          'File size is too large. Maximum size is 5MB.'
        );
        formik.setFieldTouched('BankStatement', true, false);
      } else if (filteredFile && currentFile !== null) {
        formik.setFieldError(
          'ParentRecords_BankStatement',
          'You can only upload one bank statement.'
        );
        formik.setFieldTouched('BankStatement', true, false);
        return;
      } else {
        formik.setFieldValue('BankStatement', filteredFile);
        formik.setFieldError('BankStatement', '');
      }
    },
    [formik]
  );

  const removeDocument = (type: keyof EmploymentInfo) => {
    formik?.setFieldValue(type, null);
    formik.setFieldTouched(type, false, false);
    formik.setFieldError(type, '');
  };

  const monthOptions = Array.from({ length: 13 }, (_, i) => ({
    value: String(i),
    label: String(i),
  }));

  const { data: stateOptions } = useFetchStates();

  const handleDropdownChange = (name: string, option: OptionType | null) => {
    formik.setFieldValue(name, option);
    // setIsValid(option !== null);
  };

  return (
    <div className="w-full scrollbar-always-visible">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl fomt-semibold">
          Employment & Income Information
        </h1>
        <p className="text-lg text-gray-2">Complete the fields below</p>
      </div>
      <hr className="my-4" />
      <form onSubmit={formik.handleSubmit}>
        <div className="w-full  p-4 rounded grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div className="col-span-1">
            <InputField
              type="text"
              label="Job/Occupation"
              name="Occupation"
              placeholder="e.g Bank Manager"
              value={formik.values.Occupation}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.Occupation}
            />
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="Employer's Name"
              name="EmployersName"
              placeholder="Employer's Name"
              value={formik.values.EmployersName}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.EmployersName}
            />
          </div>
          {/* Months and Years */}
          <div className="col-span-1">
            <label>
              Duration Of Working With the Company (Years & Month)
              <span className="text-red-600">*</span>
            </label>
            <div className="flex gap-4 w-full">
              <div className="w-1/2">
                <InputField
                  type="number"
                  label=""
                  name="YearsOfWorkinWithThem"
                  placeholder="Years"
                  min={0}
                  value={formik.values.YearsOfWorkinWithThem}
                  onChange={handleInputChange}
                  onBlur={formik.handleBlur}
                  required
                  error={formik.errors.YearsOfWorkinWithThem}
                />
              </div>
              <div className="w-1/2">
                <SelectInput
                  label=""
                  name="MonthsOfWorkinWithThem"
                  options={monthOptions}
                  placeholder="Select month..."
                  value={
                    monthOptions?.find(
                      (option) =>
                        option?.value === formik?.values?.MonthsOfWorkinWithThem
                    ) || null
                  }
                  onChange={(name, value) =>
                    formik?.setFieldValue(name, value?.value)
                  }
                  error={formik.errors.MonthsOfWorkinWithThem}
                />
              </div>
            </div>
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="Official Email Address"
              name="OfficialEmail"
              placeholder="abc@example.com"
              value={formik.values.OfficialEmail}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.OfficialEmail}
            />
          </div>
          <div className="col-span-1">
            <PhoneNumberInput
              label="Contact Number For Company/HR"
              name="HrContactPhoneNumber"
              inputValue={formik?.values?.HrContactPhoneNumber}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              placeholder="eg. 0812345789"
              required
              error={formik.errors.HrContactPhoneNumber}
            />
          </div>
          <CountrySelect
            value={formik.values.Country2}
            setFieldValue={formik.setFieldValue}
            onChange={handleInputChange}
            placeholder="Choose Country"
            required={true}
          />
          <div className="col-span-1">
            <InputField
              type="text"
              label="Employer's Contact Address "
              name="EmployerContactAddress1"
              placeholder="32 Ajo Lekki Phase 1"
              value={formik.values.EmployerContactAddress1}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              error={formik.errors.EmployerContactAddress1}
              required
            />
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="Employer's Address 1(Optional)"
              name="EmployerAddress1"
              placeholder="32 Ajo Lekki Phase 1"
              value={formik.values.EmployerAddress1}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              error={formik.errors.EmployerAddress1}
            />
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="Employer's Address 2(Optional)"
              name="EmployerAddress2"
              placeholder="32 Ajo Lekki Phase 2"
              value={formik.values.EmployerAddress2}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              error={formik.errors.EmployerAddress2}
            />
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="City"
              name={`City2`}
              placeholder="e.g Lagos"
              value={formik.values.City2}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={formik.errors.City2}
            />
          </div>
          <div className="col-span-1 state-input">
            <SelectInput
              label="State/Province/Region"
              options={stateOptions}
              required={true}
              name={`State2`}
              placeholder="Select state..."
              value={
                stateOptions.find(
                  (option) => option?.label === formik.values.State2
                ) || null
              }
              onChange={(name, value) => {
                formik.setFieldValue(name, value?.label);
              }}
              error={formik?.errors?.State2}
            />
          </div>
          <div className="col-span-1">
            <InputField
              type="text"
              label="Zip/Postal Code"
              name={`PostalCode2`}
              placeholder="e.g 101245"
              value={formik.values.PostalCode2}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
            />
          </div>
          <PaySlipFileUpload
            label="3 Months Payslip"
            name="ParentRecords_PaySlip"
            formik={formik}
            removeDocument={removeDocument}
          />

          <CustomFileUpload
            label="6 Months Bank Statement "
            name="BankStatement"
            formik={formik}
            removeDocument={removeDocument}
            callbackfn={onDropBankStatements}
          />
        </div>
        <Checkbox
          id="terms-checkbox"
          label="I agree to the Terms and Conditions"
          checked={isChecked}
          onChange={handleCheckboxChange}
          className="mb-4"
        />

        <div className="flex justify-end gap-3 float-end">
          <Button
            type="button"
            variant="outlined"
            onClick={() => prevStep()}
            className="w-full"
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={
              !isChecked ||
              !formik.values.BankStatement ||
              !formik.values.City2 ||
              !formik.values.EmployersName
            }
            onClick={nextStep}
            className="w-full"
          >
            {loading ? <Loader className="animate-spin" /> : 'Preview'}
          </Button>
        </div>
      </form>
      <TermsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Terms and Conditions"
        btnText="Agree"
        content={<TermsContent />}
        onConfirm={(isChecked) => handleModalConfirm(true)}
      />
    </div>
  );
};

export default MySelfStep2;
